import { ShieldAlert, AlertTriangle, Footprints, HeartHandshake, Home, MapPin, GraduationCap, Ear, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import PageHero, { NextPage } from "@/components/PageHero";
import { FadeUp, StaggerGroup, StaggerItem } from "@/components/Reveal";

const COUNTIES = ["Buckinghamshire", "London", "Hertfordshire", "Oxfordshire", "Essex", "Berkshire"];

const WHO = [
  {
    title: "Experienced exploitation",
    copy: "Young people who have experienced exploitation and need specialist, patient support to feel safe again.",
    Icon: ShieldAlert,
    color: "#FF508D",
  },
  {
    title: "At risk of criminal or gang involvement",
    copy: "Young people at risk of criminal or gang involvement, where consistent adult support can change the path.",
    Icon: AlertTriangle,
    color: "#FF9137",
  },
  {
    title: "Going missing regularly",
    copy: "Young people going missing regularly, where understanding the reasons comes before anything else.",
    Icon: Footprints,
    color: "#00A5FF",
  },
  {
    title: "Needing a trusted, consistent adult",
    copy: "Young people who simply need a trusted, consistent adult to help them navigate life's challenges.",
    Icon: HeartHandshake,
    color: "#5CFFAF",
  },
];

const WHERE = [
  { title: "At home", Icon: Home, color: "#FF508D" },
  { title: "In the community", Icon: MapPin, color: "#FF9137" },
  { title: "At school", Icon: GraduationCap, color: "#696DFF" },
];

export default function OutreachService() {
  return (
    <div data-testid="page-outreach">
      <PageHero
        testid="outreach-hero"
        eyebrow="Our outreach service"
        title={[
          "Support that meets",
          <>
            young people <em className="text-rainbow italic">where they are</em>
          </>,
        ]}
        lead="At Infinity & Beyond Care, we provide specialist outreach support for teenage boys aged 12-15 across Buckinghamshire, London, Hertfordshire, Oxfordshire, Essex, and Berkshire."
      />

      {/* Where we work */}
      <section className="container-x" data-testid="counties-section">
        <FadeUp>
          <div className="card flex flex-wrap items-center gap-3 p-7 sm:p-8" data-testid="counties-card">
            <p className="w-full text-xs font-extrabold uppercase tracking-[0.18em] text-mute sm:w-auto sm:mr-2">
              Where we work
            </p>
            {COUNTIES.map((county, i) => (
              <span key={county} className="chip bg-sand !normal-case !tracking-normal !text-sm !font-bold !px-5 !py-2.5" data-testid={`county-${i + 1}`}>
                <span className="dot" style={{ background: ["#FF508D", "#FF9137", "#FFB019", "#5CFFAF", "#00A5FF", "#696DFF"][i] }} />
                {county}
              </span>
            ))}
          </div>
        </FadeUp>
      </section>

      {/* Who we work with */}
      <section className="container-x py-16 sm:py-20 lg:py-24" data-testid="who-section">
        <FadeUp>
          <p className="eyebrow">Who we work with</p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl tracking-tight text-ink sm:text-4xl" data-testid="who-title">
            Every young person has a different story
          </h2>
        </FadeUp>
        <StaggerGroup className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {WHO.map(({ title, copy, Icon, color }) => (
            <StaggerItem key={title}>
              <div className="card h-full p-7 transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_24px_45px_-28px_rgba(45,55,72,0.45)]" data-testid={`who-${title.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 24)}`}>
                <div className="flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sand text-ink">
                    <Icon className="h-6 w-6" strokeWidth={1.5} />
                  </span>
                  <span className="dot" style={{ background: color }} aria-hidden="true" />
                </div>
                <h3 className="mt-6 font-display text-xl leading-snug text-ink">{title}</h3>
                <p className="mt-3 text-base leading-relaxed text-mute">{copy}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </section>

      {/* Where we meet them */}
      <section className="container-x" data-testid="where-section">
        <FadeUp>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-ink px-7 py-14 sm:px-14 sm:py-20">
            <div aria-hidden="true" className="absolute inset-x-0 top-0 h-1.5 bg-rainbow" />
            <p className="eyebrow !text-white/60">Meeting them where they are</p>
            <h2 className="mt-5 max-w-2xl font-display text-3xl tracking-tight text-white sm:text-4xl" data-testid="where-title">
              Genuine, non-judgemental relationships
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/70" data-testid="where-copy">
              Our outreach team builds genuine, non-judgemental relationships — meeting young people where they
              are, whether that's at home, in the community, or at school.
            </p>
            <div className="mt-10 grid gap-5 sm:grid-cols-3">
              {WHERE.map(({ title, Icon, color }) => (
                <div key={title} className="rounded-3xl border border-white/15 p-6" data-testid={`where-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <Icon className="h-6 w-6 text-sun" strokeWidth={1.5} />
                  <p className="mt-4 text-base font-bold text-white">{title}</p>
                </div>
              ))}
            </div>
          </div>
        </FadeUp>
      </section>

      {/* Our approach */}
      <section className="container-x py-16 sm:py-20 lg:py-24" data-testid="approach-section">
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <FadeUp className="lg:col-span-7">
            <p className="eyebrow">Our approach</p>
            <h2 className="mt-4 font-display text-3xl tracking-tight text-ink sm:text-4xl" data-testid="approach-title">
              We listen first, then work alongside them
            </h2>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-mute" data-testid="approach-copy">
              We listen first, then work alongside them to build confidence, safety, and positive choices,
              helping them move forward with hope and stability.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="approach-chips">
              {["Confidence", "Safety", "Positive choices", "Hope", "Stability"].map((c, i) => (
                <span key={c} className="chip">
                  <span className="dot" style={{ background: ["#FF508D", "#FF9137", "#5CFFAF", "#00A5FF", "#696DFF"][i] }} />
                  {c}
                </span>
              ))}
            </div>
          </FadeUp>
          <FadeUp delay={0.12} className="lg:col-span-5">
            <div className="card edge-rainbow p-8 sm:p-10" data-testid="listen-card">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-sand text-ink">
                <Ear className="h-7 w-7" strokeWidth={1.5} />
              </span>
              <p className="mt-6 font-display text-2xl italic leading-snug text-ink" data-testid="listen-quote">
                "We listen first, then work alongside them."
              </p>
              <p className="mt-4 text-sm leading-relaxed text-mute">
                Trusted, consistent adults — building relationships that last.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* CTA */}
      <section className="container-x pb-4" data-testid="outreach-cta">
        <FadeUp>
          <div className="flex flex-col items-start gap-6 rounded-3xl bg-sand px-7 py-8 sm:flex-row sm:items-center sm:justify-between sm:px-10">
            <p className="max-w-xl text-base font-bold text-ink" data-testid="outreach-cta-copy">
              To make an outreach referral or ask about our support, get in touch with the team.
            </p>
            <Link to="/referrals" data-testid="outreach-cta-link" className="btn btn-primary !px-5 !py-2.5 text-[13px]">
              Referrals &amp; enquiries
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </div>
        </FadeUp>
      </section>

      <div className="container-x pb-4 pt-10">
        <NextPage
          to="/referrals"
          title="Referral, matching and contact"
          label="The five-step process and how to reach us"
          testid="next-referrals-from-outreach"
        />
      </div>
    </div>
  );
}
